import { useMemo } from 'react';
import axios, { AxiosInstance, AxiosError } from 'axios';
import { useAuth0 } from '@auth0/auth0-react';

// The dashboard is a frontend-only aggregator: it calls each existing service API directly,
// using the user's Auth0 token (all services share one Auth0 tenant + audience, so a single
// access token is valid against every API). Each service has its own configurable base URL,
// exposed through a dedicated hook (useLlcApi, useCheckoutApi). Adding another service is one
// env var plus one hook built on the shared createServiceApi factory below.
const llcApiBaseUrl =
  process.env.REACT_APP_LLC_API_URL || 'https://localhost:5003';
const checkoutApiBaseUrl =
  process.env.REACT_APP_CHECKOUT_API_URL || 'http://localhost:5022';
const dashboardApiBaseUrl =
  process.env.REACT_APP_DASHBOARD_API_URL || 'http://localhost:5024';

const isAuth0LoginRequiredError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') return false;
  const err = error as { error?: string };
  const authErrorCodes = [
    'login_required',
    'consent_required',
    'interaction_required',
  ];
  return typeof err.error === 'string' && authErrorCodes.includes(err.error);
};

/**
 * Builds an Axios instance for a single backend service. Holds the shared concerns - Auth0 token
 * injection, 401 -> login redirect, 5xx/network -> error page - in one place so every per-service
 * hook behaves identically. Not exported: call sites use the named hooks below.
 */
const useServiceApi = (baseURL: string): AxiosInstance => {
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();

  return useMemo(() => {
    const instance = axios.create({
      baseURL,
      timeout: process.env.NODE_ENV === 'development' ? 0 : 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    instance.interceptors.request.use(
      async (config) => {
        if (config.data instanceof FormData) {
          delete config.headers['Content-Type'];
        }

        try {
          const token = await getAccessTokenSilently();
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          }
          return config;
        } catch (err) {
          if (isAuth0LoginRequiredError(err)) {
            await loginWithRedirect({
              appState: { returnTo: window.location.href },
            });
            return new Promise(() => {});
          }
          throw err;
        }
      },
      (error) => Promise.reject(error)
    );

    instance.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        // 401 is global (auth), so redirect to login. Everything else - server (5xx) and
        // network/connection errors - is left for the caller to handle inline (each dashboard
        // section renders its own error state). We intentionally do NOT redirect to a full-page
        // error route: that would hide the failing request and pull you off the page mid-debug.
        if (error.response?.status === 401) {
          await loginWithRedirect({
            appState: { returnTo: window.location.href },
          });
          // Redirect is underway; hang the promise so the caller's catch never runs and no inline
          // error UI flashes before the page unloads (mirrors the request interceptor above).
          return new Promise(() => {});
        }

        return Promise.reject(error);
      }
    );

    return instance;
  }, [baseURL, getAccessTokenSilently, loginWithRedirect]);
};

/** LLC API (dagforms.api) - service requests for LLC and DAG Trading. */
export const useLlcApi = (): AxiosInstance => useServiceApi(llcApiBaseUrl);

/** Checkout API (dfo.checkout.api) - the user's checkout orders. */
export const useCheckoutApi = (): AxiosInstance =>
  useServiceApi(checkoutApiBaseUrl);

/** Dashboard API (dfo.dashboard.api) - dashboard-specific endpoints (e.g. insurance intake). */
export const useDashboardApi = (): AxiosInstance =>
  useServiceApi(dashboardApiBaseUrl);
